import {
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  SquareStack,
} from "lucide-react-native";
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

import { QuestionHeader, QuestionResults } from "@/components/questions";
import CellTabBar from "@/components/runs/CellTabBar";
import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import {
  DemographicFiltersMap,
  Question,
  QuestionResults as QuestionResultsType,
  Section,
} from "@/types/resources";
import { UseCase } from "@/types/utils";
import ABTestSectionResults from "./ABTestSectionResults";
import AdCreativeSectionResults from "./AdCreativeSectionResults";
import AwarenessSectionResults from "./AwarenessSectionResults";
import ClaimTestSectionResults from "./ClaimTestSectionResults";
import MaxDiffSectionResults from "./MaxDiffSectionResults";
import MonadicSectionResults from "./MonadicSectionResults";
import NPSSectionResults from "./NPSSectionResults";
import PackagingSectionResults from "./PackagingSectionResults";
import PriceElasticitySectionResults from "./PriceElasticitySectionResults";
import TURFSectionResults from "./TURFSectionResults";

/* Use cases where a per-question detailed view is meaningful. MaxDiff is
   excluded because its "questions" are just rotation tasks of the same
   matrix question — the cooked report is the only useful lens, so those
   sections render report-only with no toggle. */
const TOGGLEABLE_USE_CASES: ReadonlySet<UseCase> = new Set([
  UseCase.PreferenceTest,
  UseCase.AdCreative,
  UseCase.Awareness,
  UseCase.ClaimTest,
  UseCase.Monadic,
  UseCase.NPS,
  UseCase.Packaging,
  UseCase.PriceElasticity,
  UseCase.TURF,
]);

/* Use cases that must always slice by cell — including the report. Most use
   cases aggregate across cells in the report and only filter in the detailed
   view, but for price elasticity each cell is a separate product, so blending
   them into a single Van Westendorp curve is meaningless. These sections keep
   the cell tab bar visible in both views and narrow the data to the active
   cell. */
const ALWAYS_CELL_FILTERED_USE_CASES: ReadonlySet<UseCase> = new Set([
  UseCase.PriceElasticity,
]);

/* ── Controller / context ──────────────────────────────── */

interface SectionResultsController {
  atEnd: boolean;
  atStart: boolean;
  cellLabels: string[];
  currentQuestion: Question | undefined;
  currentResult: QuestionResultsType | null;
  data: QuestionResultsType[];
  detailed: boolean;
  filters: DemographicFiltersMap | undefined;
  questionIndex: number;
  reportData: QuestionResultsType[];
  runId: string | null;
  section: Section;
  selectedCell: number | null;
  selectedCellIndex: number;
  setDetailed: (detailed: boolean) => void;
  setQuestionIndex: (index: number) => void;
  setSelectedCellIndex: (index: number) => void;
  showCellBar: boolean;
  showPager: boolean;
  togglable: boolean;
}

const SectionResultsContext = createContext<SectionResultsController | null>(
  null
);

/**
 * Hook to read the current `SectionResults` context — used by the sub-
 * components (`SectionResults.DetailButton`, `.Body`) so a caller can lay
 * them out anywhere in its tree while still sharing state.
 */
export const useSectionResults = (): SectionResultsController => {
  const ctx = useContext(SectionResultsContext);
  if (!ctx) {
    throw new Error(
      "useSectionResults must be used inside a <SectionResults> component"
    );
  }
  return ctx;
};

/* ── Sub-components (consume context) ──────────────────── */

/**
 * Toggle button between the composite report and the per-question detailed
 * view. Renders `null` for sections whose use case isn't togglable (e.g.
 * MaxDiff) so callers can drop it unconditionally in a toolbar.
 */
const SectionResultsDetailButton: FC = () => {
  const { i18n } = useLocalization();
  const { detailed, setDetailed, togglable } = useSectionResults();
  if (!togglable) return null;

  // The two labels have different widths ("View report" vs "View detailed"),
  // which flips per locale (in FR it's `viewReport` that's longer). Pick the
  // wider label by character count and render it as an in-flow invisible
  // ghost so the button always reserves the max width regardless of the
  // active state — the visible label then floats on top absolutely.
  const activeLabel = detailed
    ? i18n.t("studies.viewReport")
    : i18n.t("studies.viewDetailed");
  const otherLabel = detailed
    ? i18n.t("studies.viewDetailed")
    : i18n.t("studies.viewReport");
  const widerLabel =
    activeLabel.length >= otherLabel.length ? activeLabel : otherLabel;

  return (
    <Button
      className="shrink-0"
      onPress={() => setDetailed(!detailed)}
      size="sm"
      variant="outline"
    >
      <ButtonIcon as={detailed ? ChartColumn : SquareStack} />
      <Box className="relative">
        <ButtonText aria-hidden className="invisible">
          {widerLabel}
        </ButtonText>
        <ButtonText className="absolute inset-0 text-center">
          {activeLabel}
        </ButtonText>
      </Box>
    </Button>
  );
};

/**
 * Currently-paged question header — as a *separate row* under `Controls`.
 * Visibility rules (both dimensions in one component so callers can drop
 * it unconditionally):
 *  - viewing the detailed per-question view + a current question —
 *    otherwise `null`.
 *  - always visible when the cell tab bar is occupying the inline slot
 *    inside `Controls` (question text has nowhere else to go).
 *  - visible on small screens only when there are no cell tabs — the
 *    header would otherwise share the tight toolbar row with the pager
 *    and detail button. On `md+` `Controls` inlines the header in the
 *    flex-1 slot instead, so this row hides itself via `md:hidden`.
 */
const SectionResultsQuestionHeader: FC = () => {
  const { currentQuestion, detailed, section, selectedCell, showCellBar } =
    useSectionResults();
  if (!detailed || !currentQuestion) return null;
  return (
    <Box className={showCellBar ? undefined : "md:hidden"}>
      <QuestionHeader
        cell={
          selectedCell != null ? (section.cells[selectedCell] ?? null) : null
        }
        question={currentQuestion}
        variables={section.config?.variables ?? []}
      />
    </Box>
  );
};

/**
 * Section-owned controls block.
 *  - Toolbar row: `[inline slot (flex-1)] [Pager] [DetailButton]`. On
 *    `md+` the inline slot hosts either the cell tab bar or (fallback)
 *    the paged QuestionHeader; on small screens it's just an empty
 *    spacer since both would crowd the pager/toggle.
 *  - Cell tab row (small screens only, `md:hidden`): the tab bar gets
 *    its own row underneath the toolbar. On `md+` this row hides
 *    itself since the toolbar's inline slot already hosts the tabs.
 *
 * Returns `null` when nothing meaningful would render so the caller can
 * collapse the block entirely.
 */
const SectionResultsControls: FC = () => {
  const {
    atEnd,
    atStart,
    cellLabels,
    currentQuestion,
    data,
    detailed,
    questionIndex,
    section,
    selectedCell,
    selectedCellIndex,
    setQuestionIndex,
    setSelectedCellIndex,
    showCellBar,
    showPager,
    togglable,
  } = useSectionResults();

  if (!detailed && !togglable && !showCellBar && !showPager) return null;

  const inlineQuestionHeader =
    !showCellBar && detailed && currentQuestion ? currentQuestion : null;

  return (
    <VStack className="gap-3">
      <HStack className="items-center gap-3">
        <Box className="flex-1 min-w-0">
          {/* Desktop-only inline slot: tabs (preferred) or the paged
              QuestionHeader as a fallback. On small screens both live on
              their own rows so the toolbar row stays uncluttered. */}
          {showCellBar ? (
            <Box className="hidden md:flex">
              <CellTabBar
                labels={cellLabels}
                onChange={setSelectedCellIndex}
                selected={selectedCellIndex}
              />
            </Box>
          ) : inlineQuestionHeader ? (
            <Box className="hidden md:flex">
              <QuestionHeader
                cell={
                  selectedCell != null
                    ? (section.cells[selectedCell] ?? null)
                    : null
                }
                question={inlineQuestionHeader}
                variables={section.config?.variables ?? []}
              />
            </Box>
          ) : null}
        </Box>

        {/* Reserve the pager slot whenever paging is *possible* for this
            section (togglable or always-detailed, plus multi-question), even
            in report mode, so toggling into detailed mode doesn't shove the
            detail button sideways. Faded/inert until `showPager` flips on. */}
        {(togglable || detailed) && data.length > 1 && (
          <HStack
            aria-hidden={!showPager}
            className="items-center gap-2 shrink-0"
            pointerEvents={showPager ? "auto" : "none"}
            style={{ opacity: showPager ? 1 : 0 }}
          >
            <Pressable
              disabled={atStart || !showPager}
              onPress={() => setQuestionIndex(Math.max(0, questionIndex - 1))}
              style={{ opacity: atStart ? 0.3 : 1 }}
            >
              <ChevronLeft className="text-typography-700" size={20} />
            </Pressable>
            {/* Fixed-width counter so the arrows don't shuffle horizontally
                as the index grows from single to double digits. */}
            <Text className="text-xs font-medium text-typography-600 text-center min-w-[36px]">
              {questionIndex + 1} / {data.length}
            </Text>
            <Pressable
              disabled={atEnd || !showPager}
              onPress={() =>
                setQuestionIndex(Math.min(data.length - 1, questionIndex + 1))
              }
              style={{ opacity: atEnd ? 0.3 : 1 }}
            >
              <ChevronRight className="text-typography-700" size={20} />
            </Pressable>
          </HStack>
        )}

        {togglable && <SectionResultsDetailButton />}
      </HStack>

      {/* Small-screen tab row — hidden on md+ where the toolbar row
          already hosts the tabs inline. */}
      {showCellBar && (
        <Box className="md:hidden">
          <CellTabBar
            labels={cellLabels}
            onChange={setSelectedCellIndex}
            selected={selectedCellIndex}
          />
        </Box>
      )}
    </VStack>
  );
};

/**
 * The results surface: the composite report in report mode, or the
 * `QuestionResults` for the paged question in detailed mode. All chrome
 * (cell tabs, pager, detail toggle) lives in `SectionResults.Controls` —
 * the body is pure content.
 */
const SectionResultsBody: FC = () => {
  const {
    currentQuestion,
    currentResult,
    detailed,
    filters,
    reportData,
    runId,
    section,
    selectedCell,
  } = useSectionResults();

  if (detailed) {
    if (!currentResult || !currentQuestion) return null;
    return (
      <QuestionResults
        filters={filters}
        questionId={currentQuestion.id}
        result={currentResult}
        runId={runId}
        selectedCell={selectedCell}
      />
    );
  }

  return <>{renderReport(section, reportData)}</>;
};

const renderReport = (
  section: Section,
  reportData: QuestionResultsType[]
): ReactNode => {
  switch (section.use_case) {
    case UseCase.PreferenceTest:
      return <ABTestSectionResults data={reportData} section={section} />;
    case UseCase.AdCreative:
      return <AdCreativeSectionResults data={reportData} section={section} />;
    case UseCase.Awareness:
      return <AwarenessSectionResults data={reportData} section={section} />;
    case UseCase.ClaimTest:
      return <ClaimTestSectionResults data={reportData} section={section} />;
    case UseCase.MaxDiff:
      return <MaxDiffSectionResults data={reportData} section={section} />;
    case UseCase.Monadic:
      return <MonadicSectionResults data={reportData} section={section} />;
    case UseCase.NPS:
      return <NPSSectionResults data={reportData} section={section} />;
    case UseCase.Packaging:
      return <PackagingSectionResults data={reportData} section={section} />;
    case UseCase.PriceElasticity:
      return (
        <PriceElasticitySectionResults data={reportData} section={section} />
      );
    case UseCase.TURF:
      return <TURFSectionResults data={reportData} section={section} />;
    default:
      return null;
  }
};

/* ── Main component ────────────────────────────────────── */

interface SectionResultsProps {
  /**
   * Compound mode: when `children` is provided, the component just wires up
   * the context and renders `children` — the caller then drops
   * `<SectionResults.Controls>` / `<SectionResults.Body>` / individual
   * pieces (`.DetailButton`) anywhere in its tree (like
   * `MediaPicker.Trigger` / `MediaPicker.Thumbnails`). Without children the
   * default layout is used: `Controls` on top, `Body` below.
   */
  children?: ReactNode;
  /** The section's question results (already group-filtered by the caller).
      Group narrowing stays in `RunResults` so the cooked report and the
      detailed per-question view see the same data. */
  data: QuestionResultsType[];
  filters?: DemographicFiltersMap;
  runId: string | null;
  section: Section;
}

/**
 * Grouped section results. Handles two kinds of section:
 *  - **use-case**: owns the report ↔ detailed toggle.
 *    - *report*: the use-case-specific composite report (dispatched by
 *      `section.use_case`).
 *    - *detailed*: the section's questions shown one at a time via
 *      `QuestionResults`, with a pager and a cell tab bar to filter rotated
 *      answers.
 *  - **config-only** (`use_case == null && config != null`): always the
 *    detailed per-question view, with no report and no toggle button. The
 *    pager and cell tab bar behave exactly as in the use-case detailed view.
 */
const SectionResults: FC<SectionResultsProps> & {
  Body: typeof SectionResultsBody;
  Controls: typeof SectionResultsControls;
  DetailButton: typeof SectionResultsDetailButton;
  QuestionHeader: typeof SectionResultsQuestionHeader;
} = ({ children, data, filters, runId, section }) => {
  const { i18n } = useLocalization();
  const labels = useUseCaseLabels(section.use_case);
  const [detailedToggle, setDetailedToggle] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedCellIndex, setSelectedCellIndex] = useState(0);

  const togglable =
    section.use_case != null && TOGGLEABLE_USE_CASES.has(section.use_case);
  // A config-only section (no use case) has no report, so it's always in the
  // detailed per-question view; use-case sections follow their toggle.
  const detailed = section.use_case == null || (togglable && detailedToggle);

  // Reset paging, cell selection and view mode only when switching sections.
  // Every question inside a section shares the same questions/cells, so
  // navigating between them must preserve the selected cell. Done during
  // render (not in an effect) to avoid a cascading re-render.
  const [prevSectionId, setPrevSectionId] = useState(section.id);
  if (prevSectionId !== section.id) {
    setPrevSectionId(section.id);
    setDetailedToggle(false);
    setQuestionIndex(0);
    setSelectedCellIndex(0);
  }

  const currentResult = data[questionIndex] ?? null;
  const currentQuestion = currentResult?.question as Question | undefined;

  // Rotation cells for the section. Derived from all of the section's
  // questions (they always share the same cells) so the cell tab bar stays
  // stable as the user pages between questions.
  const cells = useMemo(() => {
    const set = new Set<number>();
    data.forEach((result) =>
      result.groups.forEach((g) =>
        g.answers.forEach((a) => {
          if (typeof a.cell === "number") set.add(a.cell);
        })
      )
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [data]);

  // Stable, use-case-aware cell labels: "Ad 1"/"Concept 1"/… for use-case
  // sections, "Cell 1" for free ones. Never derived from a variable value —
  // the variable values surface inside the substituted question text instead.
  const isFree = section.use_case == null;
  const cellLabels = useMemo(
    () =>
      cells.map((cell) =>
        isFree
          ? i18n.t("studies.cell", { number: cell + 1 })
          : labels.stimulusPlaceholder(cell + 1)
      ),
    [cells, i18n, isFree, labels]
  );

  const hasCells = cells.length > 1;
  const alwaysFilterByCell =
    section.use_case != null &&
    ALWAYS_CELL_FILTERED_USE_CASES.has(section.use_case);
  const showCellBar = hasCells && (detailed || alwaysFilterByCell);
  // A single-cell rotated section still stamps every answer (and its
  // summaries) with that cell, so it stays selected even without a tab bar.
  const selectedCell = showCellBar
    ? (cells[selectedCellIndex] ?? null)
    : cells.length === 1
      ? cells[0]
      : null;

  // Report payload, narrowed to the active cell when the use case requires it
  // (e.g. price elasticity). Otherwise the report sees every cell.
  const reportData = useMemo(() => {
    if (selectedCell == null) return data;
    return data.map((result) => ({
      ...result,
      groups: result.groups.map((g) => ({
        ...g,
        answers: g.answers.filter((a) => a.cell === selectedCell),
      })),
    }));
  }, [data, selectedCell]);

  const atStart = questionIndex <= 0;
  const atEnd = questionIndex >= data.length - 1;
  const showPager = detailed && data.length > 1;

  const controller: SectionResultsController = {
    atEnd,
    atStart,
    cellLabels,
    currentQuestion,
    currentResult,
    data,
    detailed,
    filters,
    questionIndex,
    reportData,
    runId,
    section,
    selectedCell,
    selectedCellIndex,
    setDetailed: setDetailedToggle,
    setQuestionIndex,
    setSelectedCellIndex,
    showCellBar,
    showPager,
    togglable,
  };

  // Compound mode: caller lays out the sub-components themselves.
  if (children !== undefined) {
    return (
      <SectionResultsContext.Provider value={controller}>
        {children}
      </SectionResultsContext.Provider>
    );
  }

  return (
    <SectionResultsContext.Provider value={controller}>
      <VStack className="gap-4">
        <SectionResultsControls />
        <SectionResultsBody />
      </VStack>
    </SectionResultsContext.Provider>
  );
};

SectionResults.Body = SectionResultsBody;
SectionResults.Controls = SectionResultsControls;
SectionResults.DetailButton = SectionResultsDetailButton;
SectionResults.QuestionHeader = SectionResultsQuestionHeader;

export default SectionResults;
